import { useCallback, useEffect, useMemo, useState } from 'react'

export interface StudioCommand {
  id: string
  icon: string
  label: string
  shortcut?: string
  run: () => void
}

export interface UseCommandPaletteApi {
  isOpen: boolean
  query: string
  setQuery: (q: string) => void
  filtered: StudioCommand[]
  open: () => void
  close: () => void
  run: (command: StudioCommand) => void
}

/** ⌘K palette — search-filtered command list + open/close state. Global ⌘K binding lives here. */
export function useCommandPalette(commands: StudioCommand[]): UseCommandPaletteApi {
  const [isOpen, setIsOpen] = useState(false)
  const [query, setQuery] = useState('')

  const open = useCallback(() => {
    setQuery('')
    setIsOpen(true)
  }, [])
  const close = useCallback(() => setIsOpen(false), [])
  const run = useCallback(
    (command: StudioCommand) => {
      close()
      command.run()
    },
    [close],
  )

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setIsOpen((prev) => {
          if (!prev) setQuery('')
          return !prev
        })
      } else if (e.key === 'Escape' && isOpen) {
        close()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [isOpen, close])

  const filtered = useMemo(
    () => commands.filter((c) => c.label.toLowerCase().includes(query.toLowerCase())),
    [commands, query],
  )

  return useMemo(() => ({ isOpen, query, setQuery, filtered, open, close, run }), [isOpen, query, filtered, open, close, run])
}
